import { Component, Input, OnInit } from '@angular/core';

import { CompareValue, OutputValue } from '../compare-value/model';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';
import { IN_USE_STATE_KEY, NOT_IN_USE_STATE_KEY, inUseState } from '@services/property/in-use-state';

import { TranslateService } from '@ngx-translate/core';
import { Options } from 'select2';

/**
 * An asset:inUse rule / deployment condition: In Use or Not In Use, optionally "for at least"
 * a duration. Emits the filter rest-api evaluates: { id: 'inList', values: [1 | 0], minDuration? }
 * with minDuration in seconds, omitted when no duration is set.
 */
@Component({
  selector: 'app-in-use-condition',
  templateUrl: './in-use-condition.component.html',
  styleUrls: ['./in-use-condition.component.scss'],
})
export class InUseConditionComponent extends CustomFormComponent implements OnInit {
  @Input() inputData: OutputValue;

  // '1' In Use, '0' Not In Use, null until chosen (the required field blocks saving it empty)
  public selectedState: string = null;
  public hours = 0;
  public minutes = 0;

  // the terms carry customer overrides, so the options read in the subscriber's own words
  public data = [
    { id: '1', text: this.translate.instant(IN_USE_STATE_KEY) },
    { id: '0', text: this.translate.instant(NOT_IN_USE_STATE_KEY) }
  ];

  public selectOptions: Options = {
    minimumResultsForSearch: Infinity,
    placeholder: this.translate.instant('DASHPAGES.CHANGING_TYPE_TS_placeholder'),
    sorter: (data) => data,
    dropdownAutoWidth: true
  };

  private _internalFormValue: OutputValue;
  private initialized = false;

  constructor(private translate: TranslateService) {
    super();
  }

  // the form builder can set formValue after ngOnInit (a reopened rule), so re-read it then
  override get formValue(): OutputValue {
    return this._internalFormValue;
  }

  override set formValue(value: OutputValue) {
    this._internalFormValue = value;
    if (this.initialized && value) {
      this.initializeFromValue(value);
    }
    this.onChanged.emit(value);
  }

  public ngOnInit() {
    this.initializeFromValue(this._internalFormValue ?? this.inputData);
    this.initialized = true;
  }

  public onStateChange(selection: { value: string }) {
    this.selectedState = selection.value;
    this.emitChanges();
  }

  public onHoursInput(event: Event): void {
    this.hours = this.readWholeNumber(event, Infinity);
    this.emitChanges();
  }

  public onMinutesInput(event: Event): void {
    this.minutes = this.readWholeNumber(event, 59);
    this.emitChanges();
  }

  // the [value] binding does not re-render when the clamped number is unchanged, so write it back:
  // the box must show the duration that will be saved (75 minutes is saved as 59, 1.5 hours as 1)
  private readWholeNumber(event: Event, max: number): number {
    const input = event.target as HTMLInputElement;
    const value = Math.min(max, Math.max(0, parseInt(input.value, 10) || 0));
    if (input.value !== '' && input.value !== String(value)) {
      input.value = String(value);
    }
    return value;
  }

  private initializeFromValue(value: OutputValue): void {
    const state = value?.values?.[0];
    this.selectedState = state === undefined || state === null || state === '' ? null : String(inUseState(state));

    const seconds = Math.max(0, +value?.minDuration || 0);
    this.hours = Math.floor(seconds / 3600);
    this.minutes = Math.floor((seconds % 3600) / 60);
  }

  private emitChanges(): void {
    let value: OutputValue = null;

    if (this.selectedState !== null && this.selectedState !== '') {
      const minDuration = ((this.hours * 60) + this.minutes) * 60;
      value = { id: CompareValue.InList, values: [+this.selectedState] };
      if (minDuration > 0) {
        value.minDuration = minDuration;
      }
    }

    this._internalFormValue = value;
    this.onChanged.emit(value);
  }
}
